import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminAPI, usersAPI, quotesAPI } from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  ArrowLeft, Save, UserX, UserCheck, Loader2,
  Building2, FileText, AlertCircle, CheckCircle2, Phone, Smartphone, Mail, Lock, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const NAVY   = 'hsl(var(--c4p-navy-900))';

// ── Set Local Password Section (SUPER_ADMIN only) ─────────────────────────────
function SetLocalPasswordSection({ userId, userEmail }) {
  const { user: currentUser } = useAuth();
  const [pw, setPw]           = useState('');
  const [pw2, setPw2]         = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [show, setShow]       = useState(false);

  if (currentUser?.role !== 'SUPER_ADMIN') return null;

  const tooShort  = pw.length > 0 && pw.length < 8;
  const hasConfirm = pw2.length > 0;
  const matches   = pw === pw2;
  const canSubmit = pw.length >= 8 && matches && !saving;

  const handleSet = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await usersAPI.setPassword(userId, pw);
      toast.success(`Local password set for ${userEmail}`);
      setPw(''); setPw2(''); setShow(false); setShowPw(false); setShowPw2(false);
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed to set password'); }
    finally { setSaving(false); }
  };

  const EyeBtn = ({ visible, toggle }) => (
    <button type="button" onClick={toggle} tabIndex={-1}
      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded"
      style={{ color: 'hsl(var(--muted-foreground))' }}>
      {visible ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
  );

  return (
    <div className="rounded-2xl border p-5 space-y-3 mt-4" style={{ borderColor: 'hsl(var(--border))' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock size={14} style={{ color: 'hsl(var(--muted-fg))' }} />
          <p className="text-sm font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>Local Password</p>
        </div>
        <button onClick={() => { setShow(v => !v); setPw(''); setPw2(''); }}
          className="text-xs font-medium hover:underline"
          style={{ color: '#0891b2' }} data-testid="toggle-set-password">
          {show ? 'Cancel' : 'Set / Change'}
        </button>
      </div>
      <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
        Sets a local email + password login for this account, bypassing Okta. Use this for test accounts.
      </p>
      {show && (
        <form onSubmit={handleSet} className="space-y-3 pt-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          {/* New password */}
          <div className="space-y-1.5">
            <Label>New password</Label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={pw}
                onChange={e => setPw(e.target.value)}
                placeholder="Minimum 8 characters"
                className="pr-9"
                style={tooShort ? { borderColor: '#fca5a5' } : {}}
                data-testid="set-password-field" autoComplete="new-password" />
              <EyeBtn visible={showPw} toggle={() => setShowPw(v => !v)} />
            </div>
            {tooShort && <p className="text-xs" style={{ color: '#ef4444' }}>At least 8 characters required</p>}
          </div>

          {/* Confirm password */}
          <div className="space-y-1.5">
            <Label>Confirm password</Label>
            <div className="relative">
              <Input type={showPw2 ? 'text' : 'password'} value={pw2}
                onChange={e => setPw2(e.target.value)}
                placeholder="Re-enter password"
                className="pr-9"
                style={hasConfirm ? { borderColor: matches ? '#86efac' : '#fca5a5' } : {}}
                data-testid="confirm-password-field" autoComplete="new-password" />
              <EyeBtn visible={showPw2} toggle={() => setShowPw2(v => !v)} />
            </div>
            {hasConfirm && (
              <p className="text-xs flex items-center gap-1" style={{ color: matches ? '#16a34a' : '#ef4444' }}>
                {matches ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
                {matches ? 'Passwords match' : 'Passwords do not match'}
              </p>
            )}
          </div>

          <Button type="submit" disabled={!canSubmit}
            style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}
            className="gap-2 w-full" data-testid="save-password-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock size={14} />}
            Set Local Password
          </Button>
        </form>
      )}
    </div>
  );
}


const ROLES  = ['SALES', 'MANAGER', 'SUPER_ADMIN', 'ACCOUNTS'];
const ROLE_LABELS = { SALES: 'Partner Success Manager', MANAGER: 'Manager', SUPER_ADMIN: 'Super Admin', ACCOUNTS: 'Accounts' };
const ROLE_COLORS = {
  SALES:      { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' },
  MANAGER:    { bg: '#fefce8', color: '#713f12', border: '#fde68a' },
  SUPER_ADMIN:{ bg: '#fef2f2', color: '#7f1d1d', border: '#fecaca' },
  ACCOUNTS:   { bg: '#f5f3ff', color: '#5b21b6', border: '#ddd6fe' },
};
const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$' };
const STATUS_STYLES = {
  DRAFT:    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  SENT:     { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' },
  ACCEPTED: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' },
  EXPIRED:  { bg: '#fffbeb', color: '#78350f', border: '#fde68a' },
};

const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';
const fmtMoney = (n, code) => `${CURRENCY_SYMBOLS[code] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RoleBadge = ({ role }) => {
  const s = ROLE_COLORS[role] || ROLE_COLORS.SALES;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{ROLE_LABELS[role] || role}</span>;
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.DRAFT;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{status}</span>;
};

// ── Profile Tab ─────────────────────────────────────────────────────────────
function ProfileTab({ user, onSaved }) {
  const [form, setForm] = useState({
    name:              user.name,
    email:             user.email,
    role:              user.role,
    phone_landline:    user.phone_landline    || '',
    phone_mobile:      user.phone_mobile      || '',
    psm_direct_email:  user.psm_direct_email  || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null); setSaved(false);
    try {
      const payload = {
        name:             form.name,
        email:            form.email,
        role:             form.role,
        phone_landline:   form.phone_landline   || null,
        phone_mobile:     form.phone_mobile     || null,
        psm_direct_email: form.psm_direct_email || null,
      };
      await adminAPI.updateUser(user.id, payload);
      toast.success('User profile updated');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl space-y-6 pt-2">
      {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}

      {/* Read-only account info */}
      <div className="rounded-xl border bg-white p-5">
        <h3 className="text-sm font-bold mb-4" style={{ color: NAVY }}>Account Information</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
          {[
            ['Status',       user.is_active !== false ? 'Active' : 'Inactive'],
            ['Last Login',   user.last_login_at ? fmtDate(user.last_login_at) : 'Never'],
            ['Member Since', fmtDate(user.created_at)],
            ['Force Reset',  user.force_reset_password ? 'Yes \u2014 pending' : 'No'],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{k}</p>
              <p className="font-medium">{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Editable fields */}
      <div className="rounded-xl border bg-white p-5">
        <h3 className="text-sm font-bold mb-4" style={{ color: NAVY }}>Edit Details</h3>
        <form onSubmit={handleSave} className="space-y-4">

          {/* Name */}
          <div className="space-y-1.5">
            <Label>Full Name *</Label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
              data-testid="user-detail-name"
            />
          </div>

          {/* Login email - read only, Okta/SSO account */}
          <div className="space-y-1.5 rounded-lg p-3" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
              <Lock size={11} /> Okta / SSO login email
            </Label>
            <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{user.email}</p>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>System login account - managed by Okta. Cannot be changed here.</p>
          </div>

          {/* Contact / Business email */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Mail size={13} style={{ color: 'hsl(var(--muted-fg))' }} /> Contact / Business Email
            </Label>
            <Input
              type="email"
              value={form.psm_direct_email}
              onChange={e => setForm(f => ({ ...f, psm_direct_email: e.target.value }))}
              placeholder="nick.jackson@ascarii.com"
              data-testid="user-detail-contact-email"
            />
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              Shown on quotes, PDFs, and signing emails. Can differ from the Okta login email above.
            </p>
          </div>

          {/* Phone numbers */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Phone size={13} style={{ color: 'hsl(var(--muted-fg))' }} /> Landline
              </Label>
              <Input
                type="tel"
                value={form.phone_landline}
                onChange={e => setForm(f => ({ ...f, phone_landline: e.target.value }))}
                placeholder="+44 20 1234 5678"
                data-testid="user-detail-landline"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Smartphone size={13} style={{ color: 'hsl(var(--muted-fg))' }} /> Mobile
              </Label>
              <Input
                type="tel"
                value={form.phone_mobile}
                onChange={e => setForm(f => ({ ...f, phone_mobile: e.target.value }))}
                placeholder="+44 7700 900000"
                data-testid="user-detail-mobile"
              />
            </div>
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="user-detail-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              PSM - own quotes/partners only. Manager - cross-team view. Super Admin - full access. Accounts - view-only access to all quotes/contracts/reports; no quote creation or admin.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2" data-testid="save-user-profile">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
              Save Changes
            </Button>
            {saved && (
              <span className="flex items-center gap-1 text-sm" style={{ color: '#15803d' }}>
                <CheckCircle2 size={14} /> Saved
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Set Local Password - SUPER_ADMIN only, allows bypassing Okta for test accounts */}
      <SetLocalPasswordSection userId={user.id} userEmail={user.email} />
    </div>
  );
}

// ── My Partners Tab ──────────────────────────────────────────────────────────
function MyPartnersTab({ userId, userName }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    usersAPI.getPartners(userId)
      .then(r => setData(r.data))
      .catch(() => setData({ partners: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="space-y-3 pt-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  const partners = data?.partners || [];

  return (
    <div className="space-y-4 pt-2">
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
        {partners.length === 0
          ? `No partners are currently assigned to ${userName}.`
          : `${partners.length} partner(s) where ${userName} is the default PSM.`}
      </p>
      {partners.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <Building2 size={32} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No partners assigned</p>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Set this user as the Default PSM on a partner record to see them here.</p>
          <Link to="/customers"><Button size="sm" variant="outline" className="mt-4 gap-1.5"><Building2 size={13} /> Go to Partners</Button></Link>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead>Company</TableHead>
                <TableHead>Partner Type</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Default Region</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map(p => (
                <TableRow key={p.id} className="data-table-row">
                  <TableCell className="font-medium">{p.company_name}</TableCell>
                  <TableCell className="text-sm">{p.partner_type || '\u2014'}</TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{p.country || '\u2014'}</TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{p.default_region_name || '\u2014'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs font-mono">{p.default_currency}</Badge></TableCell>
                  <TableCell>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={p.status === 'active' ? { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' } : { background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                      {p.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Link to={`/customers/${p.id}`}>
                      <Button variant="ghost" size="sm" className="text-xs gap-1"><ArrowLeft size={11} className="rotate-180" /> View</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── Quotes Tab ─────────────────────────────────────────────────────────────────
function QuotesTab({ userId, userName }) {
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    quotesAPI.list({ psm_user_id: userId, page_size: 50 })
      .then(r => { setQuotes(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="space-y-3 pt-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-4 pt-2">
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
        {total === 0 ? `No quotes assigned to ${userName} as PSM.` : `${total} quote(s) where ${userName} is the PSM.`}
      </p>
      {quotes.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <FileText size={32} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No quotes found</p>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Quotes where this user is the PSM will appear here.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead>Quote #</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Monthly</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map(q => (
                <TableRow key={q.id} className="data-table-row">
                  <TableCell>
                    <span className="font-mono text-sm font-semibold" style={{ color: NAVY }}>{q.quote_number}</span>
                    <span className="text-xs ml-1" style={{ color: 'hsl(var(--muted-fg))' }}>v{q.version}</span>
                  </TableCell>
                  <TableCell className="text-sm max-w-[160px] truncate">{q.customer_name || '\u2014'}</TableCell>
                  <TableCell>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={q.product_code !== 'STANDARD' ? { background: NAVY, color: 'white' } : { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }}>
                      {q.product_code}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{q.num_users}</TableCell>
                  <TableCell className="text-sm font-semibold tabular-nums">
                    {q.pricing_snapshot ? fmtMoney(q.pricing_snapshot.monthly_total ?? 0, q.currency) + '/mo' : '\u2014'}
                  </TableCell>
                  <TableCell><StatusBadge status={q.status} /></TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(q.created_at)}</TableCell>
                  <TableCell>
                    <Link to={`/quotes/${q.id}`}>
                      <Button variant="ghost" size="sm" className="text-xs gap-1"><ArrowLeft size={11} className="rotate-180" /> View</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────────
export default function UserDetailAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: users } = await adminAPI.getUsers();
      const found = (Array.isArray(users) ? users : []).find(u => u.id === id);
      if (!found) { toast.error('User not found'); navigate('/admin/users'); return; }
      setUser(found);
    } catch { toast.error('Failed to load user'); navigate('/admin/users'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async () => {
    if (user.id === currentUser?.id) return toast.error('Cannot deactivate your own account');
    try {
      await adminAPI.updateUser(user.id, { is_active: !user.is_active });
      toast.success(user.is_active !== false ? `${user.name} deactivated` : `${user.name} activated`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-16 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (!user) return null;

  const isActive = user.is_active !== false;

  return (
    <div className="space-y-5" data-testid="user-detail-page">

      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <Link to="/admin/users">
          <Button variant="ghost" size="sm"><ArrowLeft size={15} /></Button>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
              style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'hsl(218 52% 12%)' }}
            >
              {user.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{user.name}</h2>
                <RoleBadge role={user.role} />
                <Badge variant="outline" className="text-xs"
                  style={isActive
                    ? { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' }
                    : { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }
                  }>
                  {isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{user.email}</p>
              {/* Contact numbers in header */}
              {(user.phone_mobile || user.phone_landline) && (
                <div className="flex items-center gap-4 mt-0.5">
                  {user.phone_mobile && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                      <Smartphone size={11} /> {user.phone_mobile}
                    </span>
                  )}
                  {user.phone_landline && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                      <Phone size={11} /> {user.phone_landline}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {user.id !== currentUser?.id && (
          <Button variant="outline" size="sm" onClick={toggleActive} className="gap-1.5 flex-shrink-0"
            style={isActive ? { color: '#d97706', borderColor: '#fde68a' } : { color: '#15803d', borderColor: '#bbf7d0' }}
            data-testid="toggle-user-active">
            {isActive ? <UserX size={13} /> : <UserCheck size={13} />}
            {isActive ? 'Deactivate' : 'Activate'}
          </Button>
        )}
      </div>

      <Separator />

      {/* Tabs */}
      <Tabs defaultValue="profile">
        <TabsList className="mb-2">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="partners">My Partners</TabsTrigger>
          <TabsTrigger value="quotes">Quotes</TabsTrigger>
        </TabsList>
        <TabsContent value="profile"><ProfileTab user={user} onSaved={load} /></TabsContent>
        <TabsContent value="partners"><MyPartnersTab userId={user.id} userName={user.name} /></TabsContent>
        <TabsContent value="quotes"><QuotesTab userId={user.id} userName={user.name} /></TabsContent>
      </Tabs>
    </div>
  );
}
